import { Box, Grid, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { ApplicationProps, BrokerList, coparcenersType } from '../../redux-store/types/api-types';
import { formatShortDate } from '../../utils/date';
import { KeyValue } from '../ApplicationDetails/contributor';
import { Gridstyles, Item } from '../ApplicationDetails';
import { SubHeading } from '../investors/components';
import {
  checkIfApplicationIsNonIndividualPOA,
  currencyConversion,
  getclIdField,
  getdpIdField,
  isHufType,
  isCDSL,
  isOtherSourceOfFund,
  checkValidationBasedOnDate,
  getInvestorTypeMasters,
  isOtherTypeOfInvestor,
} from '../../utils/utilityFunctions';
import {
  CKYC_SELECTION_OPTIONS_OBJECT,
  DLCLMasters,
  DematDetialsValidationModified,
  SOURCE_OF_FUNDS_MASTER,
  YES_NO_BOOLEAN_MASTER,
  YES_NO_MASTER,
  investorTypeMasters,
} from '../../utils/constant';
import { useDispatch } from 'react-redux';
import { getBrokers } from '../../redux-store/actions/application';
import { styles } from '../../Styles/styles';
import { MobileViewDataRow } from '../DataTable';

export default function ContributorDetails({
  application,
}: {
  application: Partial<ApplicationProps>;
}): JSX.Element {
  const dispatch = useDispatch();
  const [brokerList, setBrokerList] = useState<BrokerList>();
  const { applicants = [], coparceners, createdAt = '' } = application || {};
  useEffect(() => {
    (async function () {
      try {
        const response = (await dispatch(getBrokers())) as unknown as BrokerList;
        setBrokerList(response as BrokerList);
      } catch (e) {
        console.error((e as Error).message);
      }
    })();
  }, []);

  const headers = [
    {
      header: 'Name',
      label: 'name',
      valueGetter: (row: coparcenersType) => row.name || 'N/A',
    },
    {
      header: 'PAN',
      valueGetter: (row: coparcenersType) => row.pan.toUpperCase() || 'N/A',
    },
    {
      header: 'Email ID',
      valueGetter: (row: coparcenersType) => row.email || 'N/A',
    },
    {
      header: 'Relation',
      valueGetter: (row: coparcenersType) =>
        row.relation === 'Others' ? row.relationShipOthers : row.relation || 'N/A',
    },
    {
      header: 'Karta',
      valueGetter: (row: coparcenersType) => (row.karta ? String('Yes') : String('No') || 'N/A'),
    },
    {
      header: 'Mobile Number',
      valueGetter: (row: coparcenersType) =>
        row.mobile && row.countryCode ? row.countryCode + '-' + row.mobile : row.mobile || 'N/A',
    },
    {
      header: 'Date of Birth',
      valueGetter: (row: coparcenersType) =>
        (row.dob ? formatShortDate(new Date(row.dob)) : 'N/A') || 'N/A',
    },
    {
      header: 'Designation',
      valueGetter: (row: coparcenersType) => row.designation || 'N/A',
    },
  ];
  const investorTypeMap = getInvestorTypeMasters(application?.createdAt || '');

  return (
    <>
      <Box sx={styles.ApplicationDetailsCommonLayout} mt={5}>
        <Box sx={{ p: 2 }}>
          <Grid container sx={{ mt: 1, pl: { xs: 2, sm: 10 }, pr: { xs: 2, sm: 10 } }}>
            <Grid container direction="row" justifyContent="space-between" alignItems="flex-start">
              <Typography sx={{ fontSize: 18, fontWeight: 600, color: 'primary.main' }}>
                Contributor Details
              </Typography>
            </Grid>
            {applicants.map((applicant) => {
              return (
                <React.Fragment key={applicant.id}>
                  <Gridstyles>
                    <Grid
                      item
                      container
                      pb={5}
                      rowSpacing={6}
                      columnSpacing={13}
                      direction="row"
                      justifyContent="space-between"
                      alignItems="flex-start">
                      <KeyValue title={'Name Of Entity'} description={applicant.name} />
                      <KeyValue
                        title={'Date of Registration/Incorporation'}
                        description={formatShortDate(applicant.dateOfBirth)}
                        sx={{ overflowWrap: 'anywhere' }}
                      />
                      <KeyValue
                        title={'Place of Registration/Incorporation'}
                        description={applicant.cityOfIncorporation}
                        sx={{ overflowWrap: 'anywhere' }}
                      />
                      <KeyValue
                        title={'CKYC selection options'}
                        description={
                          CKYC_SELECTION_OPTIONS_OBJECT[applicant.ckyc_selection_options || '']
                        }
                      />
                      <KeyValue
                        title={'CKYC No./CKYC Acknowledge No./KIN No'}
                        description={applicant.ckycNo}
                      />
                      {applicant.investorType && (
                        <KeyValue
                          title={'Investor Type'}
                          description={
                            isOtherTypeOfInvestor(applicant.investorType)
                              ? applicant?.otherInvestorType
                              : investorTypeMap[applicant.investorType]
                          }
                        />
                      )}
                      <KeyValue title={'PAN'} description={applicant.panNo} />
                      {applicant.kraMobileNumber && (
                        <KeyValue
                          title={'KRA Mobile Number'}
                          description={applicant.kraMobileNumber}
                        />
                      )}
                      {applicant.kraEmailId && (
                        <KeyValue title={'KRA Email Id'} description={applicant.kraEmailId} />
                      )}
                    </Grid>
                  </Gridstyles>
                  <SubHeading
                    sx={{
                      color: 'common.black',
                      letterSpacing: 0.5 + 'px',
                      padding: { xs: '10px 10px', sm: '10px 30px' },
                    }}>
                    {'Other Details'}
                  </SubHeading>
                  <Gridstyles>
                    <Grid
                      item
                      container
                      pb={5}
                      rowSpacing={6}
                      columnSpacing={21}
                      direction="row"
                      justifyContent="space-between"
                      alignItems="flex-start">
                      <KeyValue
                        title={'Gross Annual Income'}
                        description={applicant.grossAnnualIncome}
                      />
                      <KeyValue
                        title={'Net Worth'}
                        description={applicant.netWorth}
                        amount={currencyConversion(applicant.netWorth)}
                      />
                      <KeyValue
                        title={'PEP Status'}
                        description={applicant.politicallyExposedPersonStatus}
                      />
                      <KeyValue
                        title={'Demat Account Details (for allotment of units)'}
                        description={DLCLMasters[applicant.dlclId || '']}
                      />
                      {getdpIdField(applicant.dlclId || '') && (
                        <KeyValue
                          title={
                            isCDSL(applicant.dlclId || '') &&
                            !checkValidationBasedOnDate(createdAt, DematDetialsValidationModified)
                              ? 'DPCL ID'
                              : 'DP ID'
                          }
                          description={applicant.dpId}
                        />
                      )}
                      {getclIdField(applicant.dlclId || '', createdAt) && (
                        <KeyValue title={'CL ID'} description={applicant.clId} />
                      )}
                      {getdpIdField(applicant.dlclId || '') && (
                        <KeyValue
                          title={'Name of Broker/Depository Participant (DP)'}
                          description={
                            brokerList?.broker_list.find(
                              (broker) => broker.key === applicant.nameOfBroker
                            )?.value
                          }
                        />
                      )}
                      {getdpIdField(applicant.dlclId || '') && (
                        <KeyValue
                          title={'Name as per the Demat Account'}
                          description={applicant.dematName}
                        />
                      )}
                      <KeyValue
                        title={'AMC call verified?'}
                        description={YES_NO_MASTER[applicant.amcCallVerified || '']}
                      />
                      <KeyValue
                        title={'Source Of Funds'}
                        description={
                          isOtherSourceOfFund(applicant.sourceOfFunds || '')
                            ? applicant.sourceOfFunds
                            : SOURCE_OF_FUNDS_MASTER[applicant.sourceOfFunds || '']
                        }
                      />
                      <KeyValue
                        title={'KYC Compliant'}
                        description={
                          YES_NO_BOOLEAN_MASTER[applicant.dataFetchedFromKRA?.toString() || '']
                        }
                      />
                    </Grid>
                  </Gridstyles>
                </React.Fragment>
              );
            })}
            {isHufType(application.applicants || []) &&
              !checkIfApplicationIsNonIndividualPOA(application as ApplicationProps) && (
                <>
                  <SubHeading
                    sx={{
                      color: 'common.black',
                      letterSpacing: 0.5 + 'px',
                      padding: { xs: '10px 10px', sm: '10px 30px' },
                    }}>
                    {'List of Coparceners'}
                  </SubHeading>
                  {coparceners?.length === 0 && (
                    <Grid
                      xs={12}
                      sm={12}
                      sx={{
                        fontSize: 18,
                        fontWeight: 600,
                        color: 'common.black',
                        textAlign: 'center',
                        my: 3,
                      }}>
                      No List of Coparceners
                    </Grid>
                  )}
                  {coparceners?.map((coparceners, index) => (
                    <React.Fragment key={coparceners.id}>
                      <Item
                        sx={{
                          marginBottom: '20px',
                          paddingLeft: { xs: '10px', sm: '30px' },
                        }}>{`Coparcener ${index + 1}`}</Item>
                      <MobileViewDataRow
                        index={index}
                        row={coparceners}
                        key={coparceners.id}
                        tableHeader={headers}
                      />
                    </React.Fragment>
                  ))}
                </>
              )}
          </Grid>
        </Box>
      </Box>
    </>
  );
}
